import React, {useContext, useEffect, useState} from "react";
import {ChronicDisease, PatientPreview} from "./dataForPatientPreview";
import {excludePatientsInGroup, getPatientByFilterWithSort, getPatientsByGroup} from "./groupService";
import Alert from "../../components/modal/alert";
import Button from "../../components/button/button";
import {ButtonStatus, ButtonType} from "../../components/button/enum";
import PageButton from "../../components/pageButton/pageButton";
import {NewGroupSetting} from "./newGroup";
import {ActionType} from "../../util/enum";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {GraylogContext} from "../graylogContext";
import Spinner from "../../components/spinner/spinner";
import {PromptContext} from "../promptContext";

enum TableHeader {
    patient,
    dob,
    gender,
    phone,
    email,
    lastVisit,
    chronicDisease,
    qualityMeasure,
    lastAttempt
}

export enum PatientSortBy {
    asc = 1,
    desc = -1,
    nosort = 0
}

export interface PatientSortConfig {
    firstName: PatientSortBy,
    lastName: PatientSortBy,
    dob: PatientSortBy,
    gender: PatientSortBy,
    email: PatientSortBy,
    lastVisit: PatientSortBy,
    lastAttempt: PatientSortBy,
    openRate: PatientSortBy,
}

export const getDefaultSortByConfig = (): PatientSortConfig => {
    return {
        firstName: PatientSortBy.nosort,
        lastName: PatientSortBy.nosort,
        dob: PatientSortBy.nosort,
        gender: PatientSortBy.nosort,
        email: PatientSortBy.nosort,
        lastVisit: PatientSortBy.nosort,
        lastAttempt: PatientSortBy.nosort,
        openRate: PatientSortBy.nosort,
    }
}

export interface SelectPatient {
    clinicId: number,
    patientId: number,
}

const pageSize = 10

interface IProps {
    groupId?: string,
    groupSetting?: NewGroupSetting,
    actionType: ActionType,
    excludePatients?: Array<SelectPatient>
    onClose: (excludePatients?: Array<SelectPatient>) => void
}

function PatientPreviewList(props: IProps) {
    const {groupId, groupSetting, actionType, onClose, excludePatients = []} = props
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [patientList, setPatientList] = useState<Array<PatientPreview>>([])
    const [showSpinner, setShowSpinner] = useState<boolean>(false)
    const [excludePatientList, setExcludePatientList] = useState<Array<SelectPatient>>([])
    const [preExcludePatients, setPreExcludePatients] = useState<Array<SelectPatient>>(excludePatients)
    const [isExcludeAll, setIsExcludeAll] = useState<boolean>(false)
    const [showExcludePatientAlert, setShowExcludePatientAlert] = useState<boolean>(false)

    const defaultSortConfig: PatientSortConfig = Object.assign({}, getDefaultSortByConfig(), {firstName: PatientSortBy.asc})
    const [sortByConfig, setSortByConfig] = useState<PatientSortConfig>(defaultSortConfig)

    const logEmployee = useContext(GraylogContext)
    const {showInfo, showError} = useContext(PromptContext)

    useEffect(() => {
        // update sort and request all the data.
        if (page === 1) {
            if (sortByConfig !== defaultSortConfig) {
                getPatients()
            }
        } else {
            setPage(1)
        }
    }, [sortByConfig])

    useEffect(() => {
        getPatients()
    }, [page, preExcludePatients])

    const scrollToTop = () => {
        // window.scrollTo({
        //     top: 0,
        //     left: 0,
        //     behavior: "smooth",
        // });
    }

    const onGetPatients = (patientList: Array<PatientPreview>, total: number) => {
        let _excludePatients = excludePatientList.filter((item) => preExcludePatients.find((pt) => pt.patientId === item.patientId) === undefined)

        if (_excludePatients.length !== excludePatientList.length) {
            setExcludePatientList(_excludePatients)
        }

        let notExcludePt = patientList.find((item) => _excludePatients.find((pt) => pt.patientId === item.patientId) === undefined)
        setIsExcludeAll(notExcludePt === undefined)

        setPatientList(patientList)
        setTotal(total)
        scrollToTop()
    }

    const getPatients = () => {
        if (groupSetting) {
            let excludePatientIds = preExcludePatients.map((pt) => pt.patientId)
            setShowSpinner(true)
            getPatientByFilterWithSort(groupSetting, page, pageSize, 0, excludePatientIds, sortByConfig, (patientList, total) => {
                setShowSpinner(false)
                onGetPatients(patientList, total)
            }, (error) => {
                setShowSpinner(false)
                showError(error)
            })
        } else if (groupId) {
            setShowSpinner(true)
            getPatientsByGroup(groupId, page, pageSize, sortByConfig, (patientList, total) => {
                setShowSpinner(false)
                onGetPatients(patientList, total)
            }, (error) => {
                setShowSpinner(false)
                showError(error)
            })
        }
    }

    const startPageNumber = ((page - 1) * pageSize + 1)
    const endPageNumber = (page) * pageSize >= total ? total : (page) * pageSize
    const pageRange = `${startPageNumber}-${endPageNumber}`
    const $pageIndex = total > 0 ? (
        <div className={"flex flex-row items-center text-sm text-gray-1 space-x-2"}>
            <p className={"text-black font-bold"}>{pageRange}</p>
            <p className={"text-gray-2"}>of</p>
            <span className={"text-gray-3"}>{total}</span>
        </div>) : null

    const onNextPage = () => {
        if (page * pageSize > total) {
            return
        }
        setPage(page + 1)
    }
    const onPreviousPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const hasPreviousPage = (page > 1)
    const hasNextPage = !(page * pageSize > total)
    const $pageButton = (
        <div className={"flex flex-row items-center justify-center space-x-4"}>
            <PageButton enable={hasPreviousPage} rotate={"rotate-180"} onClick={onPreviousPage} />
            <PageButton enable={hasNextPage} onClick={onNextPage} />
        </div>
    )

    const onRemoveExcludePatients = () => {
        if (!excludePatientList.length) {
            showInfo("You must have at least one patient for this campaign.")
            return;
        }
        // exclude patients from this campaign
        if (groupId) {
            excludePatientsInGroup(groupId, excludePatientList, () => {
                setPreExcludePatients(preExcludePatients.concat(excludePatientList))
                GrayLog.event(LogEventName.removeFromGroup, {isSuccessful: true, ...logEmployee})
            }, (error) => {
                alert("remove fail...")
                GrayLog.event(LogEventName.removeFromGroup, {isSuccessful: false, errMsg: `${error.code} - ${error.msg}`, ...logEmployee})
            })
        } else {
            setPreExcludePatients(preExcludePatients.concat(excludePatientList))
        }
    }

    const onClickExclude = () => {
        if (!excludePatientList.length) {
            showInfo("You must have at least one patient for this campaign.")
            return
        }
        setShowExcludePatientAlert(true)
    }

    const renderExcludeButton = () => {
        if (actionType === ActionType.view) {
            return <div />
        }
        return (
            <Button status={(excludePatientList.length > 0 && excludePatientList.length < total) ? ButtonStatus.primary : ButtonStatus.disabled} type={ButtonType.outline} onClick={onClickExclude}>{"Remove from this group"}</Button>
        )
    }

    const $pageView = (
        <div className={"flex flex-row items-center justify-between space-x-20"}>
            {$pageIndex}
            {$pageButton}
        </div>
    )

    const $footer = total > 0 ? (
        <div className={"flex flex-row items-center justify-between w-full pt-4"}>
            {renderExcludeButton()}
            {$pageView}
        </div>
    ) : null

    const $emailRow = (value: string | number) => {
        return <p className={"w-36 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{value}</p>
    }

    const $dateRow = (value: string | number) => {
        return <p className={"w-24 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{value}</p>
    }

    const $genderRow = (value: string) => {
        return <p className={"w-16 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{value}</p>
    }

    const getChronicDiseaseName = (status: ChronicDisease): [string, string, string] => {
        if (status === ChronicDisease.diabetes) {
            return ["Diabetes", "text-white", "bg-diabetes"]
        } else if (status === ChronicDisease.asthma) {
            return ["Asthma", "text-white", "bg-asthma"]
        } else if (status === ChronicDisease.hypertension) {
            return ["Hypertension", "text-white", "bg-hypertension"]
        } else if (status === ChronicDisease.copd) {
            return ["COPD", "text-white", "bg-copd"]
        } else if (status === ChronicDisease.chronicDisease) {
            return ["ChronicDisease", "text-red", "bg-red-30"]
        }
        return ["", "", ""]
    }

    const $chronicDiseaseItem = (status: ChronicDisease, idx: number) => {
        const [title, statusTextColor, statusTextBgColor] = getChronicDiseaseName(status)
        if (!title.length) {
            return null
        }
        return (
            <div key={idx} className={`flex-none py-1 px-1.5 rounded ${statusTextBgColor} max-w-max`}>
                <p className={`text-xs font-semibold ${statusTextColor} max-w-max`}>{title}</p>
            </div>
        )
    }

    const $qualityMeasureRow = (qualityMeasures: Array<string>) => {
        const qmList = qualityMeasures.map((qm) => qm).join(", ")
        return (
            <p className={"w-44 text-gray-2 text-sm font-normal"}>{qmList}</p>
        )
    }

    const resortChronicDiseases = (chronicDiseases: Array<ChronicDisease>) => {
        const len = chronicDiseases.length / 2
        const list = []
        for (let i = 0; i < len; i ++) {
            const subList: Array<ChronicDisease> = []
            subList.push(chronicDiseases[i * 2])
            if ((i * 2 + 1) < chronicDiseases.length) {
                subList.push(chronicDiseases[i * 2 + 1])
            }
            list.push(subList)
        }
        return list
    }
    const $chronicDiseaseRow = (chronicDiseases: Array<ChronicDisease>) => {
        const list = resortChronicDiseases(chronicDiseases)
        return (
            <div className={"w-50"}>
                <div className={"w-max flex-none flex-wrap space-y-1"}>
                    {list.map((group, idx) => {
                        return <div key={idx} className={"flex flex-row flex-wrap space-x-1"}>
                            {group.map((cd, _idx) => {
                                return $chronicDiseaseItem(cd, _idx)
                            })}
                        </div>
                    })}
                </div>
            </div>
        )
    }

    const $phone = (icon: any, phoneNumber: string) => {
        return (
            <div className={"flex flex-row items-center space-x-1"}>
                {icon}
                <p className={"min-w-min flex-none text-gray-2 text-sm font-normal"}>{phoneNumber}</p>
            </div>
        )
    }

    const $iconMobilePhone = (
        <svg className={"flex-none"} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_453_704)">
                <path d="M14.1005 10.4668L11.038 9.15432C10.9072 9.09856 10.7618 9.08681 10.6237 9.12084C10.4856 9.15487 10.3623 9.23283 10.2724 9.34299L8.91613 11C6.78762 9.99645 5.07466 8.28349 4.07109 6.15498L5.72812 4.79873C5.83851 4.70896 5.91663 4.58566 5.95067 4.44752C5.98472 4.30937 5.97283 4.16389 5.9168 4.03311L4.6043 0.970607C4.5428 0.829625 4.43405 0.714517 4.29678 0.645134C4.15951 0.57575 4.00233 0.556438 3.85234 0.590528L1.00859 1.24678C0.863992 1.28017 0.734977 1.36159 0.642607 1.47775C0.550237 1.5939 0.499967 1.73794 0.5 1.88635C0.5 8.90002 6.18476 14.5738 13.1875 14.5738C13.336 14.5739 13.4801 14.5237 13.5963 14.4313C13.7125 14.339 13.7939 14.2099 13.8273 14.0653L14.4836 11.2215C14.5175 11.0708 14.4977 10.913 14.4278 10.7752C14.3579 10.6375 14.2422 10.5284 14.1005 10.4668Z" fill="black"/>
            </g>
            <defs>
                <clipPath id="clip0_453_704">
                    <rect width="14" height="14" fill="white" transform="translate(0.5 0.57373)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const $iconHomePhone = (
        <svg className={"flex-none"} width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_453_708)">
                <path d="M7.31466 3.95504L2.83345 7.64584V11.6293C2.83345 11.7324 2.87442 11.8313 2.94735 11.9043C3.02028 11.9772 3.1192 12.0182 3.22234 12.0182L5.94601 12.0111C6.04881 12.0106 6.14723 11.9694 6.21974 11.8965C6.29225 11.8237 6.33296 11.725 6.33296 11.6222V9.29595C6.33296 9.19281 6.37393 9.09389 6.44686 9.02096C6.51979 8.94803 6.6187 8.90706 6.72184 8.90706H8.2774C8.38054 8.90706 8.47945 8.94803 8.55238 9.02096C8.62531 9.09389 8.66629 9.19281 8.66629 9.29595V11.6205C8.66613 11.6717 8.67607 11.7224 8.69554 11.7697C8.71501 11.817 8.74363 11.8601 8.77976 11.8963C8.81589 11.9325 8.85881 11.9613 8.90607 11.9809C8.95333 12.0005 9.004 12.0106 9.05517 12.0106L11.7779 12.0182C11.881 12.0182 11.9799 11.9772 12.0529 11.9043C12.1258 11.8313 12.1668 11.7324 12.1668 11.6293V7.64317L7.68653 3.95504C7.63386 3.91259 7.56824 3.88943 7.50059 3.88943C7.43294 3.88943 7.36733 3.91259 7.31466 3.95504ZM14.3931 6.46362L12.3612 4.78872V1.42216C12.3612 1.34481 12.3305 1.27062 12.2758 1.21592C12.2211 1.16122 12.1469 1.13049 12.0695 1.13049H10.7084C10.6311 1.13049 10.5569 1.16122 10.5022 1.21592C10.4475 1.27062 10.4168 1.34481 10.4168 1.42216V3.18699L8.2407 1.39664C8.03187 1.22479 7.76982 1.13084 7.49938 1.13084C7.22893 1.13084 6.96689 1.22479 6.75806 1.39664L0.605606 6.46362C0.576072 6.48803 0.551637 6.51802 0.533698 6.55188C0.515758 6.58574 0.504666 6.6228 0.501054 6.66094C0.497443 6.69909 0.501383 6.73757 0.51265 6.7742C0.523916 6.81082 0.542289 6.84486 0.566717 6.87438L1.18651 7.62785C1.21087 7.65748 1.24083 7.682 1.27469 7.70003C1.30854 7.71806 1.34562 7.72923 1.38379 7.73291C1.42197 7.73659 1.4605 7.73271 1.49717 7.72148C1.53384 7.71025 1.56794 7.6919 1.59751 7.66747L7.31466 2.95851C7.36733 2.91606 7.43294 2.89291 7.50059 2.89291C7.56824 2.89291 7.63386 2.91606 7.68653 2.95851L13.4039 7.66747C13.4334 7.6919 13.4675 7.71027 13.5041 7.72154C13.5407 7.73281 13.5792 7.73675 13.6174 7.73314C13.6555 7.72952 13.6926 7.71843 13.7264 7.70049C13.7603 7.68255 13.7903 7.65812 13.8147 7.62858L14.4345 6.87511C14.4589 6.84542 14.4772 6.8112 14.4883 6.77442C14.4994 6.73764 14.5032 6.69901 14.4993 6.66078C14.4955 6.62254 14.4841 6.58543 14.4659 6.5516C14.4477 6.51776 14.423 6.48786 14.3931 6.46362Z" fill="black"/>
            </g>
            <defs>
                <clipPath id="clip0_453_708">
                    <rect width="14" height="12.4444" fill="white" transform="translate(0.5 0.351562)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const $phoneRow = (mobilePhone: string, homePhone: string) => {
        return (
            <div>
                {mobilePhone && mobilePhone.length ? $phone($iconMobilePhone, mobilePhone) : null}
                {homePhone && homePhone.length ? $phone($iconHomePhone, homePhone) : null}
            </div>
        )
    }

    const isExcluded = (excludePatient: SelectPatient) => {
        return excludePatientList.find((pt) => pt.patientId === excludePatient.patientId) !== undefined
    }

    const onChangeExclude = (patient: PatientPreview) => {
        let excludePatient: SelectPatient = {
            clinicId: patient.clinicId,
            patientId: patient.patientId,
        }

        let _excludePatients = excludePatientList.slice()
        if (isExcluded(excludePatient)) {
            _excludePatients = _excludePatients.filter((pt) => pt.patientId !== excludePatient.patientId)
        } else {
            _excludePatients.push(excludePatient)
        }
        setExcludePatientList(_excludePatients)
    }

    const $activeCampaignContent = () => {
        return patientList.map((patientInfo, idx) => {
            return (
                <tr className={"border-b border-border-black-12"} key={idx}>
                    <td className={"p-1"}>
                        <div className={"flex flex-row items-center w-40"}>
                            {actionType === ActionType.view ? null : <input checked={excludePatientList.find((pt) => pt.patientId === patientInfo.patientId) !== undefined} onChange={() => {
                                onChangeExclude(patientInfo)
                            }} type="checkbox" className="form-checkbox h-3.5 w-3.5 rounded mr-4" />}
                            <p className={"w-full break-all text-gray-1 text-sm"}>{patientInfo.name}</p>
                        </div>
                    </td>
                    <td className={"p-1"}>{$dateRow(patientInfo.dob)}</td>
                    <td className={"p-1"}>{$genderRow(patientInfo.gender)}</td>
                    <td className={"p-1"}>{$phoneRow(patientInfo.mobilePhone, patientInfo.homePhone)}</td>
                    <td className={"p-1"}>{$emailRow(patientInfo.email)}</td>
                    <td className={"p-1"}>{$dateRow(patientInfo.lastVisit)}</td>
                    <td className={"p-1"}>{$chronicDiseaseRow(patientInfo.chronicDisease)}</td>
                    <td className={"p-1"}>{$qualityMeasureRow(patientInfo.qualityMeasures)}</td>
                    <td className={"p-1"}>{$dateRow(patientInfo.lastAttempt)}</td>
                </tr>
            )
        })
    }

    const $title = (<div className={"flex flex-row items-center justify-between"}>
        <p className={"text-lg text-gray-1 font-bold"}>Preview Patients</p>
        <Button status={ButtonStatus.float} onClick={() => {
            onClose && onClose(preExcludePatients.length !== excludePatients.length ? preExcludePatients : undefined)
        }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 4.5L14.0806 14.0806" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.5 14.0811L14.0806 4.50041" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </Button>
    </div>)

    const getSelectedExcludePatients = () => {
        return excludePatientList.length ? `, ${excludePatientList.length} selected` : ""
    }
    const $statisticStatusHeader = (<div className={"flex flex-row items-center"}>
        <p className={"text-sm text-gray-2 mt-4"}>{`${total} ${total > 1 ? "Patients" : "Patient"}`}</p>
        <p className={"text-sm text-gray-2 mt-4"}>{getSelectedExcludePatients()}</p>
    </div>)
    const $header = (
        <div className={"w-full text-left"}>
            {$title}
            {$statisticStatusHeader}
        </div>
    )

    const $iconArrow = (
        <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_454_1044)">
                <path d="M0.694613 4.25H5.72C6.06766 4.25 6.24149 4.66992 5.99539 4.91602L3.48368 7.42969C3.33133 7.58203 3.08328 7.58203 2.93094 7.42969L0.419222 4.91602C0.173129 4.66992 0.346957 4.25 0.694613 4.25Z" fill="#333333"/>
            </g>
            <defs>
                <clipPath id="clip0_454_1044">
                    <rect width="6.25" height="10" fill="white" transform="translate(0.083313 0.5)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const onChangeSortBy = (type: TableHeader) => {
        const newConfig = getDefaultSortByConfig()
        if (type === TableHeader.patient) {
            newConfig.firstName = sortByConfig.firstName === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.dob) {
            newConfig.dob = sortByConfig.dob === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.gender) {
            newConfig.gender = sortByConfig.gender === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.email) {
            newConfig.email = sortByConfig.email === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.lastVisit) {
            newConfig.lastVisit = sortByConfig.lastVisit === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.lastAttempt) {
            newConfig.lastAttempt = sortByConfig.lastAttempt === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        }
        setSortByConfig(newConfig)
    }

    const renderTableHeaderTitle = (type: TableHeader, title: string) => {
        // if (type === TableHeader.patient) {
        //     return $patientToolTips
        // } else if (type === TableHeader.lastAttempt) {
        //     return $lastAttemptToolTips
        // }
        return <p className={"w-max font-bold text-sm text-gray-1"}>{title}</p>
    }

    const renderCheckbox = (type: TableHeader) => {
        if (type === TableHeader.patient && actionType !== ActionType.view) {
            return <input checked={isExcludeAll} onChange={onExcludeCurrentPagePatients} type="checkbox" className="form-checkbox h-3.5 w-3.5 rounded mr-4" />
        }
        return null
    }

    const renderTableHeaderItem = (type: TableHeader, title: string, sortable: boolean = false, sortBy: PatientSortBy = PatientSortBy.nosort) => {
        const $sortBtn = (
            <button type={"button"} onClick={() => {
                onChangeSortBy(type)
            }} className={`p-2 rounded-full ${sortBy === PatientSortBy.nosort ? "opacity-0 group-hover:opacity-100 bg-primary-light" : "hover:bg-primary-light"} cursor-pointer`}>
                <div className={`w-4 h-4 flex items-center justify-center transform ${sortBy === PatientSortBy.asc ? "rotate-180" : "rotate-0"}`}>
                    {$iconArrow}
                </div>
            </button>
        )
        return (
            <th>
                <div className={"relative w-full my-4 px-1 flex flex-row items-center justify-between"}>
                    {renderCheckbox(type)}
                    <div className={"flex-1 flex flex-row items-center justify-between group"}>
                        {renderTableHeaderTitle(type, title)}
                        {sortable ? $sortBtn : null}
                    </div>
                    {/*{type !== TableHeader.lastAttempt ? <div className={"w-0.5 h-5 bg-gray-200 absolute right-0"}/> : null}*/}
                </div>
            </th>
        )
    }

    const onExcludeCurrentPagePatients = () => {
        const patients: Array<SelectPatient> = patientList.map((pt) => ({
            clinicId: pt.clinicId,
            patientId: pt.patientId,
        }))
        let _excludePatients = excludePatientList.slice()
        if (isExcludeAll) {
            _excludePatients = _excludePatients.filter((item) => patients.find((pt) => pt.patientId === item.patientId) === undefined)
        } else {
            _excludePatients = _excludePatients.concat(patients.filter((item) => _excludePatients.find((pt) => pt.patientId === item.patientId) === undefined))
        }
        setExcludePatientList(_excludePatients)
        setIsExcludeAll(!isExcludeAll)
    }

    // const $patientToolTips = (
    //     <ToolTips tips={"Uncheck patients to exclude them from receiving messages."}>
    //         <p className={"cursor-default text-sm font-bold text-gray-1"}>Patient</p>
    //     </ToolTips>
    // )
    //
    // const $lastAttemptToolTips = (
    //     <ToolTips alignStyle={ToolTipAlign.right} tips={"The last time the practice dispatched an appointment office visit, phone encounter, or message for this patient."}>
    //         <p className={"cursor-default text-sm font-bold text-gray-1"}>Last Attempt</p>
    //     </ToolTips>
    // )

    const $tableHeader = (
        <tr className={"text-left text-gray-2 font-bold border-b border-base-border text-sm"}>
            {renderTableHeaderItem(TableHeader.patient, "Patient", true, sortByConfig.firstName)}
            {renderTableHeaderItem(TableHeader.dob, "DOB", true, sortByConfig.dob)}
            {renderTableHeaderItem(TableHeader.gender, "Gender")}
            {renderTableHeaderItem(TableHeader.phone, "Phone")}
            {renderTableHeaderItem(TableHeader.email, "Email")}
            {renderTableHeaderItem(TableHeader.lastVisit, "Last Visit", true, sortByConfig.lastVisit)}
            {renderTableHeaderItem(TableHeader.chronicDisease, "Chronic Disease")}
            {renderTableHeaderItem(TableHeader.qualityMeasure, "Quality Measures")}
            {renderTableHeaderItem(TableHeader.lastAttempt, "Last Attempt", true, sortByConfig.lastAttempt)}
        </tr>
    )

    const $table = (
        <table className={"w-full"}>
            <thead>
                {$tableHeader}
            </thead>
            <tbody>
                {$activeCampaignContent()}
            </tbody>
        </table>
    )

    let titleAlertForExclude = ""
    let descriptionAlertForExclude = ""
    if (actionType !== ActionType.view) {
        titleAlertForExclude = "Remove from this group"
        descriptionAlertForExclude = "Are you sure you want to remove patients from this group?"
    }
    const $excludeSelectedPatientAlert = (
        <Alert
            isOpen={showExcludePatientAlert}
            title={titleAlertForExclude}
            description={descriptionAlertForExclude}
            rejectAction={{title: "Cancel", onClick: () => {
                setShowExcludePatientAlert(false)
            }}
            }
            resolveAction={{title: "Confirm", onClick: () => {
                setShowExcludePatientAlert(false)
                onRemoveExcludePatients()
            }}
            }
        />
    )

    const $spinner = <Spinner isOpen={showSpinner}/>

    return (
        <div className={"w-full h-full flex flex-col items-center bg-white rounded p-4"}>
            {$header}
            {$table}
            {$footer}
            {$excludeSelectedPatientAlert}
            {$spinner}
        </div>
    )
}

export default PatientPreviewList
